import { BdayLogo } from "@/components/bday-logo";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { isEmbeddedInApp } from "@/lib/native-bridge";

// Set these when the BirthdayAlarm mobile apps ship (PRD 12); the banner
// stays hidden while they're empty.
const IOS_STORE_URL = "";
const ANDROID_STORE_URL = "";
const DISMISSED_KEY = "bday:install-banner-dismissed";

type MobileOS = "ios" | "android" | null;

function detectMobileOS(): MobileOS {
  if (typeof window === "undefined") return null;

  // Hide inside the native app's webview (RN bridge object or ?embed=1).
  if (isEmbeddedInApp()) return null;

  const ua = navigator.userAgent;

  if (/android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";

  // iPadOS 13+ reports as Mac; detect via touch capability
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return "ios";

  return null;
}

export function MobileInstallBanner() {
  const [os, setOs] = useState<MobileOS>(null);
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    setOs(detectMobileOS());
    try {
      setDismissed(localStorage.getItem(DISMISSED_KEY) === "1");
    } catch {
      setDismissed(false);
    }
  }, []);

  if (!os || dismissed) return null;

  const storeUrl: string = os === "ios" ? IOS_STORE_URL : ANDROID_STORE_URL;
  const storeLabel = os === "ios" ? "App Store" : "Google Play";
  if (!storeUrl) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      // ignore storage errors
    }
    setDismissed(true);
  };

  return (
    <div
      role="region"
      aria-label="Install BirthdayAlarm app"
      className="fixed inset-x-0 bottom-0 z-50 bg-background/95 backdrop-blur border-t border-border shadow-[0_-4px_12px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom)]"
    >
      <div className="flex items-center gap-4 px-4 py-4">
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="shrink-0 text-muted-foreground hover:text-foreground p-1"
        >
          <X className="w-5 h-5" />
        </button>
        <BdayLogo size={32} aria-hidden />
        <div className="flex-1 min-w-0">
          <p className="text-base text-muted-foreground leading-snug truncate">
            Get the app on the {storeLabel}
          </p>
        </div>
        <a
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 text-base font-semibold bg-foreground text-background py-2.5 px-5 rounded-full hover:opacity-90 transition-opacity"
        >
          Get
        </a>
      </div>
    </div>
  );
}
